// "Also known as" — the per-record alias editor for a vector-searchable MASTER.
//
// The point of the button is that some things a record is called never appear
// in its data. Robert Sharma is "Bob"; the Head Office is "HQ"; a supplier is
// known internally by a short code nobody typed into the name field. Search
// can't find any of them from the record alone, and no amount of better
// matching will change that — the information isn't there. So a user who knows
// it teaches it once, and the alias is folded into the record's vector.
//
// Shared by every surface (designer live preview, deployed runtime, downloaded
// app): the HTTP call is injected, exactly as RecordHistoryModal does it, so
// the package carries no client.

import { useEffect, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { parseApiError } from '../utils'

/** Reads a record's aliases. Injected by the hosting app. */
export type AliasFetcher = (
  project: string, objName: string, txnId: string,
) => Promise<{ ok?: boolean; aliases: string[] }>

/** Replaces a record's aliases. Injected by the hosting app. */
export type AliasWriter = (
  project: string, objName: string, txnId: string, aliases: string[],
) => Promise<{ ok?: boolean; aliases: string[] }>

interface Props {
  project: string
  objName: string
  txnId: string
  /** What the record is called — shown so the user knows which one they are
   *  teaching, which matters most in exactly the same-name case aliases fix. */
  recordLabel?: string
  onClose: () => void
  fetchAliases: AliasFetcher
  saveAliases: AliasWriter
}

export default function RecordAliasModal({
  project, objName, txnId, recordLabel, onClose, fetchAliases, saveAliases,
}: Props) {
  const [aliases, setAliases] = useState<string[] | null>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setAliases(null)
    setError(null)
    fetchAliases(project, objName, txnId)
      .then((r) => { if (!cancelled) setAliases(r.aliases || []) })
      .catch((e) => { if (!cancelled) setError(parseApiError(e).message) })
    return () => { cancelled = true }
  }, [project, objName, txnId, fetchAliases])

  const add = () => {
    const value = draft.trim().replace(/\s+/g, ' ')
    if (!value || aliases === null) return
    // Case-insensitive dedupe: "Bob" and "bob" are one alias, and adding the
    // second silently would look like the first didn't save.
    if (aliases.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setDraft('')
      return
    }
    setAliases([...aliases, value])
    setDraft('')
  }

  const remove = (i: number) => {
    if (aliases === null) return
    setAliases(aliases.filter((_, idx) => idx !== i))
  }

  const save = async () => {
    if (aliases === null) return
    setSaving(true)
    setError(null)
    try {
      // Fold in whatever is still sitting in the input — a user who typed an
      // alias and pressed Save without pressing + means to keep it.
      const pending = draft.trim().replace(/\s+/g, ' ')
      const finalList = pending &&
        !aliases.some((a) => a.toLowerCase() === pending.toLowerCase())
        ? [...aliases, pending]
        : aliases
      const res = await saveAliases(project, objName, txnId, finalList)
      setAliases(res.aliases || finalList)
      onClose()
    } catch (e) {
      setError(parseApiError(e).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
          borderRadius: 12, padding: 20, minWidth: 380, maxWidth: 520,
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Also known as</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--color-text-secondary)', margin: '0 0 14px', lineHeight: 1.5 }}>
          {recordLabel
            ? <>Other names people use for <strong>{recordLabel}</strong>.</>
            : <>Other names people use for this record.</>}
          {' '}Anyone searching by one of these will find it, even though the
          name appears nowhere in the record itself.
        </p>

        {error && (
          <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 14, marginBottom: 10 }}>
            {error}
          </div>
        )}

        {aliases === null && !error && (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: '12px 0' }}>
            Loading…
          </div>
        )}

        {aliases !== null && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                autoFocus
                value={draft}
                placeholder="e.g. Bob"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
                style={{
                  flex: 1, padding: '7px 10px', fontSize: 14,
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
                }}
              />
              <button className="btn btn-sm" onClick={add} disabled={!draft.trim()}
                      style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <Plus size={14} /> Add
              </button>
            </div>

            {aliases.length === 0 ? (
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', padding: '4px 0 12px' }}>
                No aliases yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                {aliases.map((a, i) => (
                  <span
                    key={`${a}-${i}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 8px', fontSize: 13.5, borderRadius: 999,
                      background: 'var(--color-bg-secondary, #f1f5f9)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    {a}
                    <button
                      onClick={() => remove(i)}
                      title={`Remove ${a}`}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 0, display: 'flex', color: 'var(--color-text-secondary)',
                      }}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
